import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';

export interface InlineFeedbackNoteProps {
  entityKey: string;
  placeholder?: string;
  initialText?: string;
  /** ms debounce voor “Opgeslagen” */
  autosaveDebounceMs?: number;
  onAutosave: (text: string) => void;
  onSendHeadsUp: (text: string) => void;
}

const InlineFeedbackNote: React.FC<InlineFeedbackNoteProps> = ({
  entityKey,
  placeholder = 'Schrijf feedback voor de recruiter…',
  initialText = '',
  autosaveDebounceMs = 900,
  onAutosave,
  onSendHeadsUp,
}) => {
  const [text, setText] = useState(initialText);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setText(initialText);
  }, [entityKey, initialText]);

  const scheduleSave = useCallback(
    (value: string) => {
      if (timer.current) clearTimeout(timer.current);
      setSaveState('saving');
      timer.current = setTimeout(() => {
        onAutosave(value);
        setSaveState('saved');
        timer.current = null;
      }, autosaveDebounceMs);
    },
    [autosaveDebounceMs, onAutosave],
  );

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 shadow-inner">
      <textarea
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          scheduleSave(v);
        }}
        rows={3}
        placeholder={placeholder}
        className="w-full resize-none border-0 bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400"
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200/80 pt-2">
        <span className="text-xs font-semibold text-slate-500">
          {saveState === 'saving' && 'Opslaan…'}
          {saveState === 'saved' && 'Opgeslagen'}
          {saveState === 'idle' && '\u00a0'}
        </span>
        <button
          type="button"
          onClick={() => onSendHeadsUp(text)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700"
        >
          <Send className="h-3.5 w-3.5" aria-hidden />
          Verstuur heads-up
        </button>
      </div>
    </div>
  );
};

export default InlineFeedbackNote;
